import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import { BulkOpsRequest, FieldCheckRequest, FieldCheckResult, GridDefinition, GridPage, GridQuery, LookupOption, LookupRequest, PreCheckResult } from '../grid-types';
import { FormatURI, HttpService } from '../../../utility/http-service';

@Injectable({ providedIn: 'root' })
export class GridApiService {
  // Session-level lookup cache — keyed by "{gridKey|dash:dashboardId}:{lookupKey}".
  // Survives component destroy/recreate; cleared only on full page reload.
  private readonly _lookupCache = new Map<string, LookupOption[]>();

  // Session-level grid definition cache. A grid's column layout/config is stable for
  // the session, so re-navigating to the same grid shouldn't re-fetch it. We store and
  // return clones so a component mutating its own copy (e.g. dashboards disabling edit)
  // never pollutes the cached definition.
  private readonly _defCache = new Map<string, GridDefinition>();

  constructor(private http: HttpService) {}

  /**
   * Drops both caches. Grid definitions and lookup options are shaped by the
   * caller's role/company, so any change of identity (impersonate, exit
   * impersonation) must invalidate them or a subsequent user inherits data
   * scoped to whoever loaded it first this session.
   */
  clearCache(): void {
    this._defCache.clear();
    this._lookupCache.clear();
  }

  definition(gridKey: string, onSuccess: Function) {
    const cached = this._defCache.get(gridKey);
    if (cached) { onSuccess(structuredClone(cached)); return; }
    return this.http.get<GridDefinition>(`Grid/${gridKey}/definition`, (def: GridDefinition) => {
      this._defCache.set(gridKey, structuredClone(def));
      onSuccess(def);
    });
  }

  data(gridKey: string, q: GridQuery, onSuccess: Function) {
    return this.http.postReturn<GridPage>(`Grid/${gridKey}/data`, onSuccess, undefined, q);
  }

  lookup(gridKey: string, lookupKey: string, req: LookupRequest, onSuccess: Function) {
    // Include the request signature in the cache key so cascading lookups
    // (whose results depend on the supplied filter) aren't poisoned by a
    // prior empty-filter response.
    const key = `${gridKey}:${lookupKey}:${this.reqCacheKey(req)}`;
    const cached = this._lookupCache.get(key);
    if (cached) { onSuccess(cached); return; }
    return this.http.postReturn<LookupOption[]>(
      `Grid/${gridKey}/lookups/${lookupKey}`,
      (opts: LookupOption[]) => { this._lookupCache.set(key, opts); onSuccess(opts); },
      undefined, req);
  }

  dashboardLookup(dashboardId: string, lookupKey: string, req: LookupRequest, onSuccess: Function) {
    const key = `dash:${dashboardId}:${lookupKey}:${this.reqCacheKey(req)}`;
    const cached = this._lookupCache.get(key);
    if (cached) { onSuccess(cached); return; }
    return this.http.postReturn<LookupOption[]>(
      `Grid/${dashboardId}/dashboardLookups/${lookupKey}`,
      (opts: LookupOption[]) => { this._lookupCache.set(key, opts); onSuccess(opts); },
      undefined, req);
  }

  private reqCacheKey(req: LookupRequest): string {
    // Stable key — only the bits the server uses to shape the result.
    return JSON.stringify({ f: req.filter ?? null, s: req.search ?? null, c: req.context ?? null });
  }

  bulkOps(gridKey: string, req: BulkOpsRequest, onSuccess: Function, onError: Function) {
    return this.http.postReturn<void>(`Grid/${gridKey}/bulkOps`, onSuccess, undefined, req, onError);
  }
  /** Advisory warnings to confirm before saving. A failed check must not stop the save, so errors still call onSuccess with none. */
  preCheck(gridKey: string, req: BulkOpsRequest, onSuccess: (r: PreCheckResult) => void) {
    return this.http.postReturn<PreCheckResult>(
      `Grid/${gridKey}/preCheck`, onSuccess, undefined, req, () => onSuccess({ warnings: [] }));
  }
  /**
   * Messages about one field of a record still being added. Runs in the background while the user types, so unlike
   * the other calls it shows no spinner and no error dialog: any failure just yields no messages.
   * Returned as an Observable so a newer check can cancel one still in flight.
   */
  fieldCheck(gridKey: string, req: FieldCheckRequest): Observable<FieldCheckResult> {
    return this.http.http.post<any>(FormatURI(`Grid/${gridKey}/fieldCheck`), req).pipe(
      map(x => (x && 'errorIndicator' in x) ? { messages: [] } : {
        messages: x?.messages ?? [], values: x?.values ?? null, blocked: !!x?.blocked, lockedFields: x?.lockedFields ?? null
      }),
      catchError(() => of({ messages: [] })));
  }
  dashboardDefinition(dashboardId: string, onSuccess: Function) {
    const key = `dash:${dashboardId}`;
    const cached = this._defCache.get(key);
    if (cached) { onSuccess(structuredClone(cached)); return; }
    return this.http.get<GridDefinition>(`Grid/${dashboardId}/dashboardDefinition`, (def: GridDefinition) => {
      this._defCache.set(key, structuredClone(def));
      onSuccess(def);
    });
  }

  dashboardData(dashboardId: string, q: GridQuery, onSuccess: Function) {
    return this.http.postReturn<GridPage>(`Grid/${dashboardId}/dashboardData`, onSuccess, undefined, q);
  }
}
